import { CommandOriginEnum, CommandTypeEnum, ICommandDTO } from '@halter-corp/cowtroller-service-client';

const errorMessage = (command: ICommandDTO): string =>
  // New type or miss mapped a possible combination
  `Unmapped combination, ask an engineer to update: raw message: ${command.type}, ${command.context.origin}`;

const getCommandsToRemove = (commandArguments: any): string => {
  const commandsToRemove = commandArguments.commandsToRemove as Array<{
    commandId: string;
    collarCommandId: string;
  }>;
  const collarCommandIds =
    commandsToRemove.length > 1
      ? commandsToRemove.map((it) => it.collarCommandId)
      : commandsToRemove[0].collarCommandId;
  return JSON.stringify(collarCommandIds);
};

const getRemovingCommandIds = (commandArguments: object): string => {
  const { commandIdsToRemove } = commandArguments as any;
  return commandIdsToRemove != null && (commandIdsToRemove as string[]).length > 0
    ? ` - Removing ${(commandIdsToRemove as string[]).join(', ')}`
    : '';
};

const prettyPrintSetZone = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.BREAK_SHAPE_UPDATED:
      return 'Break update - shape or assigned mobs updated';
    case CommandOriginEnum.UPCOMING_BREAK: {
      const { useSharedBoundaryControlToZone } = command.arguments as any;
      const { isEnterPaddockOnly } = command.arguments as any;
      return useSharedBoundaryControlToZone
        ? 'Beef shared boundary transition - Locked In'
        : `Upcoming - Next break ${isEnterPaddockOnly ? '(Enter paddock only)' : ''}`;
    }
    case CommandOriginEnum.DROP_FENCE_SHIFT_TO_NEXT: {
      const { useSharedBoundaryControlToZone, isFlowBackAllowed } = command.arguments as any;
      const isExtension = useSharedBoundaryControlToZone === true && isFlowBackAllowed === true;
      return `Break to break - Next break${isExtension ? ' (Extension)' : ''}`;
    }
    case CommandOriginEnum.BREAK_TO_BREAK_NEW_AREA_SHIFT:
      return 'Beef shared boundary transition - Flow Back Allowed';
    case CommandOriginEnum.DROP_FENCE_EXIT_PADDOCK:
    case CommandOriginEnum.DROP_FENCE_NO_SHIFT: {
      const { isEnterPaddockOnly } = command.arguments as any;
      return `Paddock cooldown - ${isEnterPaddockOnly ? '"Enter only"' : 'Next'} break`;
    }
    case CommandOriginEnum.SET_BREAK:
    case CommandOriginEnum.SET_BREAK_SHIFT: {
      const label =
        command.context.origin === CommandOriginEnum.SET_BREAK ? 'Set break' : 'Set break with shift to break';
      return `${label}${getRemovingCommandIds(command.arguments)}`;
    }
    default:
      return errorMessage(command);
  }
};

const prettyPrintExitZone = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.EXIT_PADDOCK_SHIFT: {
      const isDraft = command.groupId == null;
      if (isDraft) {
        return 'Exit paddock shift - Drafting';
      }

      if (
        (command.arguments as any).exitPath != null &&
        (command.arguments as any).exitPath.coordinates.length > 0
      ) {
        return 'Exit paddock shift - With race path';
      }

      return 'Exit paddock shift - To gate';
    }

    default:
      return errorMessage(command);
  }
};

const prettyPrintPauseCommands = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.INCIDENT:
      return 'Pre-emptive pause incident';
    case CommandOriginEnum.GROUP_PAUSE:
      return 'Group pause';
    default:
      return errorMessage(command);
  }
};

const prettyPrintDeleteCommands = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.INCIDENT:
      return `Resolving pause incident - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.EXIT_PADDOCK_SHIFT:
      return `Finishing exit paddock command - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.BREAK_DELETED:
    case CommandOriginEnum.BREAK_TO_BREAK_NEW_AREA_SHIFT:
      return `B2B2B V1 : Break deleted - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.BACK_TO_PADDOCK_SHIFT:
      return `Completing shift back to paddock - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.GROUP_PAUSE:
      return `Removing group pause - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.REMOVE_COMMANDS:
      return `Delete command(s) - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.DROP_FENCE_NO_SHIFT:
      return `Drop break - Manual shift - ${getCommandsToRemove(command.arguments)}`;
    case CommandOriginEnum.BREAK_CANCELLED:
      return `Break cancelled - ${getCommandsToRemove(command.arguments)}`;
    default:
      return errorMessage(command);
  }
};

const prettyPrintClearCommands = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.DROP_FENCE_NO_SHIFT:
      return 'Drop break - Manual shift';
    case CommandOriginEnum.BREAK_CANCELLED:
      return 'Break cancelled';
    default:
      return errorMessage(command);
  }
};

const prettyPrintBackToPaddock = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.BACK_TO_PADDOCK_SHIFT:
      return 'Back to paddock shift';
    default:
      return errorMessage(command);
  }
};

const prettyPrintFlow = (command: ICommandDTO): string => {
  switch (command.context.origin) {
    case CommandOriginEnum.FLOW_SHIFT: {
      const { zoneType } = command.arguments as any;
      const flowTypeLabel = zoneType != null ? (zoneType as string) : 'Unknown';
      return `Flow shift for ${flowTypeLabel}${getRemovingCommandIds(command.arguments)}`;
    }
    default:
      return errorMessage(command);
  }
};

export const prettyPrintOrigin = (command: ICommandDTO): string => {
  switch (command.type) {
    case CommandTypeEnum.SET_ZONE:
      return prettyPrintSetZone(command);
    case CommandTypeEnum.EXIT_ZONE:
      return prettyPrintExitZone(command);
    case CommandTypeEnum.PAUSE:
      return prettyPrintPauseCommands(command);
    case CommandTypeEnum.DELETE_COMMANDS:
      return prettyPrintDeleteCommands(command);
    case CommandTypeEnum.CLEAR_STATE:
      return prettyPrintClearCommands(command);
    case CommandTypeEnum.BACK_TO_PADDOCK:
      return prettyPrintBackToPaddock(command);
    case CommandTypeEnum.FLOW:
      return prettyPrintFlow(command);
    default:
      return errorMessage(command);
  }
};

export const prettyPrintType = (type: CommandTypeEnum): string => {
  switch (type) {
    case CommandTypeEnum.CLEAR_STATE:
      return 'Clear Zone';
    case CommandTypeEnum.SET_ZONE:
      return 'Set Zone';
    case CommandTypeEnum.EXIT_ZONE:
      return 'Exit Paddock';
    case CommandTypeEnum.DELETE_COMMANDS:
      return 'Delete Commands';
    case CommandTypeEnum.PAUSE:
      return 'Pause Command';
    case CommandTypeEnum.BACK_TO_PADDOCK:
      return 'Back To Paddock';
    case CommandTypeEnum.FLOW:
      return 'Flow';
    default:
      return type;
  }
};
